import { Link } from "react-router-dom";
import "./home.css";
import MusicPlayer from "../components/MusicPlayer";
import TikTokSection from "../components/TikTokSection";

const BioSkeleton = ({ lines = 3 }) => (
  <div className="bio-skeleton" aria-hidden="true">
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className={`skeleton-line ${
          i === lines - 1 ? "skeleton-line--short" : "skeleton-line--full"
        }`}
      />
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Fallback values — shown while homeConfig is loading, and as
// a safety net if Firestore is unavailable.
// ─────────────────────────────────────────────────────────────
const HERO_IMAGE_FALLBACK =
  "https://firebasestorage.googleapis.com/v0/b/anjalidmusic.firebasestorage.app/o/images%2FAnjaliShoot_042024-6.jpg?alt=media&token=323671f6-48a2-4e4f-82bd-40165df8a967";

const FEATURED_FALLBACK = {
  title: "Girl on the Train",
  description:
    "My latest single - a story of connection, dreams, and the moments that change everything.",
  spotifyUrl: "https://open.spotify.com/track/7rJiLV0jXqcLesKQSvCniD",
  imageUrl:
    "https://firebasestorage.googleapis.com/v0/b/anjalidmusic.firebasestorage.app/o/images%2Flarge%20rectangle%20pictures%20near%20train.jpeg?alt=media&token=f896e4f6-3d22-4334-8f95-3849bf29890b",
  bgPositionY: 0,
};

// bio and homeConfig both come from App.jsx — fetched once, never re-fetched on navigation
const Home = ({ bio, homeConfig }) => {
  // Resolve config values: use Firestore data when available, fall back to constants
  const heroImage = homeConfig?.heroImageUrl || HERO_IMAGE_FALLBACK;

  const featured = {
    title: homeConfig?.featuredTitle || FEATURED_FALLBACK.title,
    description:
      homeConfig?.featuredDescription || FEATURED_FALLBACK.description,
    spotifyUrl: homeConfig?.featuredSpotifyUrl || FEATURED_FALLBACK.spotifyUrl,
    imageUrl: homeConfig?.featuredImageUrl || FEATURED_FALLBACK.imageUrl,
    bgPositionY:
      homeConfig?.featuredBgPositionY ?? FEATURED_FALLBACK.bgPositionY,
  };

  return (
    <div className="home-page">
      {/* ── Hero Section ───────────────────────────────────── */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <br />
            <h1 className="hero-title text-gradient">Anjali D Music</h1>
            <p className="hero-subtitle">Singer • Songwriter • Storyteller</p>

            <div className="bio-load-area">
              {bio ? (
                <p
                  className="hero-description bio-fade-in"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {bio.homePageBioOne}
                </p>
              ) : (
                <BioSkeleton lines={3} />
              )}
            </div>

            <div className="hero-buttons">
              <Link to="/music" className="btn-primary">
                Listen Now
              </Link>
              <Link to="/bio" className="btn-secondary">
                About Me
              </Link>
            </div>
          </div>

          {/* Hero image — fades in alongside the bio to prevent layout jump */}
          <div
            className={`hero-visual ${
              homeConfig ? "bio-fade-in-delayed" : "hero-visual--hidden"
            }`}
          >
            <div className="hero-image">
              <img
                src={heroImage}
                alt="Anjali Daswani"
                className="hero-photo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured Song Banner ────────────────────────────── */}
      <section
        className="featured-song-section"
        style={{
          backgroundImage: `url("${featured.imageUrl}")`,
          backgroundPositionY: `${featured.bgPositionY}%`,
        }}
      >
        <div className="featured-song-overlay">
          <div className="container">
            <div className="featured-content">
              <h2>{featured.title}</h2>
              <p>{featured.description}</p>
              <div className="featured-buttons">
                <a
                  href={featured.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Listen on Spotify
                </a>
                <Link to="/music" className="btn-secondary">
                  More Music
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Music Section ───────────────────────────────────── */}
      <section className="music-section">
        <div className="container">
          <h2>Latest Music</h2>
          <MusicPlayer />
        </div>
      </section>

      {/* ── About Preview Section ───────────────────────────── */}
      <section className="about-preview-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About Anjali</h2>

              <div className="bio-load-area">
                {bio ? (
                  <p className="bio-fade-in" style={{ whiteSpace: "pre-wrap" }}>
                    {bio.homePageBioTwo}
                  </p>
                ) : (
                  <BioSkeleton lines={3} />
                )}
              </div>

              <Link to="/bio" className="btn-secondary">
                Read More
              </Link>
            </div>

            <div className="about-image">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/anjalidmusic.firebasestorage.app/o/images%2Fgraduation%20picture.jpg?alt=media&token=89f847b6-c1d3-4e84-bd16-532606c80a00"
                alt="Anjali Daswani graduation"
                className="about-photo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TikTok Videos Section ───────────────────────────── */}
      <TikTokSection />
    </div>
  );
};

export default Home;
